import { useEffect, useState, useCallback } from "react";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import AppointmentDialog from "@/components/AppointmentDialog";
import { toast } from "sonner";

const STATUS_COLORS = { confirmed: "#2C3E35", completed: "#7A8B5C", cancelled: "#B86B5A", no_show: "#C9A227" };
const FILTERS = ["all", "confirmed", "completed", "cancelled", "no_show"];

export default function Appointments() {
  const { currency } = useAuth();
  const { t, lang } = useI18n();
  const loc = lang === "es" ? "es-ES" : "en-US";
  const [appts, setAppts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/appointments");
    setAppts(data);
  }, []);

  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/staff").then(({ data }) => setStaff(data));
    api.get("/services").then(({ data }) => setServices(data));
  }, [load]);

  const del = async (id) => {
    await api.delete(`/appointments/${id}`);
    toast.success(t.appts.deleted);
    load();
  };

  const filtered = filter === "all" ? appts : appts.filter((a) => a.status === filter);
  const sorted = [...filtered].sort((a, b) => new Date(b.start) - new Date(a.start));

  return (
    <div className="fade-up space-y-6" data-testid="appointments-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="label-caps">{t.appts.overline}</p>
          <h1 className="font-heading text-3xl font-light tracking-tight mt-1">{t.appts.title}</h1>
        </div>
        <button data-testid="new-appointment-btn" onClick={() => setDialog({ open: true, initial: null })}
          className="px-4 py-2.5 rounded-lg bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#1E2B25] transition-colors inline-flex items-center gap-2">
          <Plus size={16} weight="bold" /> {t.appts.newAppt}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filter === f ? "bg-[#2C3E35] text-white" : "bg-white border border-[#E5E2DC] text-[#6B6A67] hover:bg-[#E8E4DF]"
            }`}>
            {f === "all" ? t.st.all : t.st[f]}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2DC] overflow-hidden">
        {sorted.length === 0 ? (
          <div className="py-16 text-center text-[#6B6A67] text-sm">{t.appts.none}</div>
        ) : (
          <div className="divide-y divide-[#E5E2DC]">
            {sorted.map((a) => (
              <div key={a.id} data-testid={`appt-row-${a.id}`} className="flex items-center gap-4 px-5 py-3.5 hover:bg-[#FDFBF9] transition-colors group">
                <div className="w-1 h-11 rounded-full shrink-0" style={{ background: a.staff_color }} />
                <div className="w-28 shrink-0">
                  <div className="text-sm font-medium">{new Date(a.start).toLocaleDateString(loc, { month: "short", day: "numeric" })}</div>
                  <div className="text-xs text-[#6B6A67]">{new Date(a.start).toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{a.customer_name}</div>
                  <div className="text-xs text-[#6B6A67] truncate">{a.service_name} · {a.staff_name}</div>
                </div>
                <div className="text-sm font-medium hidden sm:block">{money(a.price, currency)}</div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full hidden sm:inline" style={{ background: `${STATUS_COLORS[a.status]}18`, color: STATUS_COLORS[a.status] }}>{t.st[a.status]}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-appt-${a.id}`} onClick={() => setDialog({ open: true, initial: a })} className="p-2 rounded-lg text-[#6B6A67] hover:bg-[#E8E4DF]"><PencilSimple size={16} /></button>
                  <button data-testid={`del-appt-${a.id}`} onClick={() => del(a.id)} className="p-2 rounded-lg text-[#6B6A67] hover:bg-[#B86B5A]/12 hover:text-[#B86B5A]"><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AppointmentDialog
        open={dialog.open} initial={dialog.initial}
        onClose={() => setDialog({ open: false, initial: null })}
        onSaved={load} customers={customers} staff={staff} services={services}
      />
    </div>
  );
}
